import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search,
  Building2,
  Briefcase,
  Users,
  Mail,
  Clock,
  X,
  Activity,
  FolderKanban,
  Wallet,
  AlertTriangle,
  Download,
  Eye,
  FileUp,
  FilePlus,
  Send,
  Tag,
  BellRing,
  FileText,
  Lock,
  Upload,
  Trash2,
  KeyRound,
  Trash,
  ShieldCheck,
  Pencil,
  Store,
} from "lucide-react";
import { api } from "../lib/api";

const ACTION_META = {
  login: { label: "Accesso alla piattaforma", icon: KeyRound },
  profile_updated: { label: "Dati profilo aggiornati (da admin)", icon: Pencil },
  project_create: { label: "Nuovo progetto creato", icon: FilePlus },
  project_submit: { label: "Progetto inviato", icon: Send },
  project_file_upload: { label: "File progetto caricato", icon: FileUp },
  project_file_download: { label: "File progetto scaricato", icon: Download },
  project_file_preview: { label: "File progetto visualizzato", icon: Eye },
  asset_download: { label: "Asset fornitore scaricato", icon: Download },
  asset_preview: { label: "Asset fornitore visualizzato", icon: Eye },
  discount_request: { label: "Richiesta sconto fornitore", icon: Tag },
  magic_search: { label: "Magic Search", icon: Search },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  return Math.floor((Date.now() - d) / 86400000);
}

function ActivityDescription({ a }) {
  const action = a.action || "other";
  if (action === "project_create" || action === "project_submit") {
    return <span className="text-slate1">{a.project_name || "—"}{a.client_name ? ` · ${a.client_name}` : ""}</span>;
  }
  if (action === "project_file_upload" || action === "project_file_download" || action === "project_file_preview") {
    return <span className="text-slate1">{a.filename || ""} {a.project_name ? `· ${a.project_name}` : ""}</span>;
  }
  if (action === "asset_download" || action === "asset_preview") {
    return <span className="text-slate1">{a.asset_title || ""}{a.supplier_name ? ` · ${a.supplier_name}` : ""}</span>;
  }
  if (action === "discount_request") {
    return <span className="text-slate1">Fornitore: {a.supplier_name || "—"}</span>;
  }
  if (action === "magic_search") {
    return (
      <span className="text-slate1">
        <b className="text-ink">&ldquo;{a.query || ""}&rdquo;</b>
        {a.supplier_name ? <> su <span className="text-ink">{a.supplier_name}</span></> : null}
        {typeof a.total_matches === "number" ? <span className="text-slate1/70"> · {a.total_matches} risultati</span> : null}
      </span>
    );
  }
  return <span className="text-slate1">—</span>;
}

/**
 * Anello SVG con la % di successo di un utente (volume chiuso / valore valorizzato).
 *
 *  - Colore: emerald >= 70%, amber 40-69%, rose < 40%.
 *  - Etichetta centrale: percentuale grande.
 *  - Riga sotto: "€ chiuso / € valorizzato".
 */
function SuccessRateRing({ rate = 0, closed = 0, valorized = 0 }) {
  const safeRate = Math.max(0, Math.min(100, Number(rate) || 0));
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - safeRate / 100);
  const colorClass = safeRate >= 70
    ? "text-emerald-600"
    : safeRate >= 40
      ? "text-amber-500"
      : "text-rose-500";
  const bgClass = safeRate >= 70
    ? "bg-emerald-50 border-emerald-200"
    : safeRate >= 40
      ? "bg-amber-50 border-amber-200"
      : "bg-rose-50 border-rose-200";
  return (
    <div className={`mt-3 ${bgClass} border rounded-sm px-3 py-2.5 flex items-center gap-3`} data-testid="success-rate-ring">
      <svg width="56" height="56" viewBox="0 0 56 56" className="shrink-0">
        <circle cx="28" cy="28" r={radius} fill="none" stroke="currentColor" strokeWidth="5" className="text-ink/10" />
        <circle
          cx="28" cy="28" r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={colorClass}
          transform="rotate(-90 28 28)"
          style={{ transition: "stroke-dashoffset 0.6s ease-out" }}
        />
        <text x="28" y="32" textAnchor="middle" className={`${colorClass} font-display`} style={{ fontSize: 13, fontWeight: 600 }} fill="currentColor">
          {Math.round(safeRate)}%
        </text>
      </svg>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">% Successo</div>
        <div className="font-display text-sm text-ink truncate">
          € {Math.round(closed || 0).toLocaleString("it-IT")}
          <span className="text-slate1 text-xs"> / € {Math.round(valorized || 0).toLocaleString("it-IT")}</span>
        </div>
        <div className="text-[9px] text-slate1 italic">Chiuso vs valorizzato</div>
      </div>
    </div>
  );
}

function UserCard({ u, onOpen, onSendReminder, reminderSending }) {
  const inactiveDays = daysSince(u.last_login_at);
  const isInactive = u.is_inactive;
  const isAdmin = u.role === "admin";
  const isTrader = u.account_type === "trader";
  const isRealEstate = u.account_type === "real_estate";
  const accountLabel = isAdmin ? "Admin" : isTrader ? "Trader" : isRealEstate ? "Real Estate" : "Studio";
  const accountColor = isAdmin ? "text-violet-700" : isTrader ? "text-blue-700" : isRealEstate ? "text-emerald-700" : "text-gold";
  const AccountIcon = isAdmin ? ShieldCheck : isTrader ? Briefcase : isRealEstate ? Users : Building2;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(u)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(u); } }}
      className="text-left bg-white border border-ink/10 rounded-sm p-5 hover:border-ink/30 hover:shadow-sm transition-all relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-ink/20"
      data-testid={`admin-user-${u.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 ${accountColor}`}>
            <AccountIcon size={11} /> {accountLabel}
          </div>
          <div className="mt-1 font-display text-lg text-ink truncate">{u.name || "—"}</div>
          <div className="text-sm text-slate1 truncate">{u.studio || "—"}</div>
        </div>
        {isInactive && (
          <span
            title={inactiveDays != null ? `Ultimo accesso ${inactiveDays} giorni fa` : "Mai loggato"}
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-sm"
          >
            <AlertTriangle size={10} /> Inattivo
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-xs text-slate1 truncate">
        <Mail size={12} /> {u.email}
      </div>

      {isRealEstate ? (
        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-slate1">Contatti</div>
            <div className="mt-0.5 font-display text-lg text-ink inline-flex items-center gap-1 justify-center">
              <Users size={13} className="text-slate1" /> {u.contacts_count || 0}
            </div>
          </div>
          <div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-slate1">Confermati</div>
            <div className="mt-0.5 font-display text-lg text-ink">{u.contacts_confirmed || 0}</div>
          </div>
          <div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-slate1">Wallet</div>
            <div className="mt-0.5 font-display text-lg text-ink inline-flex items-center gap-1 justify-center">
              <Wallet size={13} className="text-slate1" /> € {Math.round(u.wallet_credit).toLocaleString("it-IT")}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-4 gap-3 text-center">
            <div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-slate1">Progetti</div>
              <div className="mt-0.5 font-display text-lg text-ink inline-flex items-center gap-1 justify-center">
                <FolderKanban size={13} className="text-slate1" /> {u.project_count}
              </div>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-slate1">Valorizzato</div>
              <div className="mt-0.5 font-display text-lg text-ink" title="Somma del valore di tutti i progetti valorizzati (incluso chiuso)">
                € {Math.round(u.valorized_total || 0).toLocaleString("it-IT")}
              </div>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-slate1">Volume chiuso</div>
              <div className="mt-0.5 font-display text-lg text-ink">€ {Math.round(u.closed_total).toLocaleString("it-IT")}</div>
            </div>
            {isTrader ? (
              <div title="Numero di fornitori per cui ha richiesto lo sconto studio">
                <div className="text-[9px] tracking-[0.2em] uppercase text-slate1">Fornitori richiesti</div>
                <div className="mt-0.5 font-display text-lg text-ink inline-flex items-center gap-1 justify-center" data-testid={`user-requested-suppliers-${u.id}`}>
                  <Tag size={13} className="text-slate1" /> {u.requested_suppliers_count || 0}
                </div>
              </div>
            ) : (
              <div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-slate1">Wallet</div>
                <div className="mt-0.5 font-display text-lg text-ink inline-flex items-center gap-1 justify-center">
                  <Wallet size={13} className="text-slate1" /> € {Math.round(u.wallet_credit).toLocaleString("it-IT")}
                </div>
              </div>
            )}
          </div>
          {(u.valorized_total || 0) > 0 && (
            <SuccessRateRing rate={u.success_rate || 0} closed={u.closed_total} valorized={u.valorized_total} />
          )}
        </>
      )}

      <div className="mt-3 pt-3 border-t border-ink/10 flex items-center justify-between text-[11px] text-slate1">
        <span className="inline-flex items-center gap-1"><Clock size={11} /> {u.last_login_at ? fmtDate(u.last_login_at) : "Mai"}</span>
        <span>Iscritto {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}</span>
      </div>

      {isTrader && u.admin_files_count > 0 && (
        <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase text-gold bg-gold/10 border border-gold/30 px-2 py-1 rounded-sm">
          <Lock size={10} /> {u.admin_files_count} {u.admin_files_count === 1 ? "Dichiarazione" : "Dichiarazioni"} d&apos;intento
        </div>
      )}

      {isInactive && (
        <div className="mt-3 pt-3 border-t border-ink/10 flex items-center justify-between gap-3">
          <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">
            {u.last_reminder_at ? `Richiamato il ${new Date(u.last_reminder_at).toLocaleDateString("it-IT")}` : "Nessun richiamo inviato"}
          </span>
          <button
            onClick={(e) => onSendReminder(u, e)}
            disabled={!!reminderSending[u.id]}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gold/40 text-gold text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-gold hover:text-cream disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid={`admin-user-remind-${u.id}`}
          >
            <BellRing size={12} />
            {reminderSending[u.id] ? "Invio..." : "Invia richiamo"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountType, setAccountType] = useState("all"); // all | studio | trader
  const [showInactive, setShowInactive] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null); // user object
  const [activity, setActivity] = useState(null); // {user, activities}
  const [activityLoading, setActivityLoading] = useState(false);
  const [reminderSending, setReminderSending] = useState({}); // {uid: true}
  const [adminFiles, setAdminFiles] = useState([]);
  const [adminFilesLoading, setAdminFilesLoading] = useState(false);
  const [userDiscounts, setUserDiscounts] = useState([]);
  const [userDiscountsLoading, setUserDiscountsLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [resettingPwd, setResettingPwd] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [savingAccountType, setSavingAccountType] = useState(false);
  // Create-user modal (admin crea profilo pre-approvato)
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Edit-user modal (correzione dati anagrafici)
  const [showEditModal, setShowEditModal] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (accountType !== "all") params.account_type = accountType;
      if (showInactive) params.inactive = true;
      const { data } = await api.get("/admin/users", { params });
      // Quando il filtro è "Tutti", carichiamo anche gli admin separatamente
      // (il backend li esclude di default per compatibilità storica) e li
      // mergiamo alla lista in coda.
      if (accountType === "all" && !showInactive) {
        try {
          const adminParams = { account_type: "admin" };
          const { data: adminData } = await api.get("/admin/users", { params: adminParams });
          const seen = new Set(data.map((u) => u.id));
          const extra = (adminData || []).filter((u) => !seen.has(u.id));
          setUsers([...data, ...extra]);
          return;
        } catch { /* silente — usa solo la lista base */ }
      }
      setUsers(data);
    } catch {
      toast.error("Errore caricamento utenti");
    } finally {
      setLoading(false);
    }
  }, [accountType, showInactive]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(ql) ||
      (u.name || "").toLowerCase().includes(ql) ||
      (u.studio || "").toLowerCase().includes(ql)
    );
  }, [users, q]);

  const studios = filtered.filter((u) => u.role !== "admin" && (u.account_type || "studio") === "studio");
  const traders = filtered.filter((u) => u.role !== "admin" && u.account_type === "trader");
  const realEstates = filtered.filter((u) => u.role !== "admin" && u.account_type === "real_estate");
  const admins = filtered.filter((u) => u.role === "admin");

  async function openActivity(u) {
    setSelected(u);
    setActivity(null);
    setAdminFiles([]);
    setUserDiscounts([]);
    setActivityLoading(true);
    // Sconti riservati assegnati all'utente (fornitore per fornitore)
    setUserDiscountsLoading(true);
    api.get(`/admin/users/${u.id}/supplier-discounts`)
      .then(({ data }) => setUserDiscounts(data))
      .catch(() => {})
      .finally(() => setUserDiscountsLoading(false));
    try {
      const { data } = await api.get(`/admin/users/${u.id}/activity`, { params: { limit: 10 } });
      setActivity(data);
    } catch {
      toast.error("Errore caricamento attività");
    } finally {
      setActivityLoading(false);
    }
    // Load admin-only files (only for traders, where the Dichiarazioni d'intento applies)
    if (u.account_type === "trader") {
      setAdminFilesLoading(true);
      try {
        const { data } = await api.get(`/admin/users/${u.id}/files`);
        setAdminFiles(data);
      } catch (err) {
        console.error("[admin-users] load admin files failed", err);
      } finally {
        setAdminFilesLoading(false);
      }
    }
  }

  function closePanel() {
    setSelected(null);
    setActivity(null);
    setAdminFiles([]);
    setUserDiscounts([]);
    setShowEditModal(false);
  }

  async function uploadAdminFile(file) {
    if (!file || !selected) return;
    setUploadingFile(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "dichiarazione_intento");
    fd.append("title", file.name);
    try {
      await api.post(`/admin/users/${selected.id}/files`, fd);
      toast.success("File caricato");
      const { data } = await api.get(`/admin/users/${selected.id}/files`);
      setAdminFiles(data);
      setUsers((prev) => prev.map((x) => x.id === selected.id ? { ...x, admin_files_count: data.length } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setUploadingFile(false);
    }
  }

  async function deleteAdminFile(fid) {
    if (!selected) return;
    if (!window.confirm("Eliminare questo file?")) return;
    try {
      await api.delete(`/admin/users/${selected.id}/files/${fid}`);
      const { data } = await api.get(`/admin/users/${selected.id}/files`);
      setAdminFiles(data);
      setUsers((prev) => prev.map((x) => x.id === selected.id ? { ...x, admin_files_count: data.length } : x));
      toast.success("Eliminato");
    } catch {
      toast.error("Errore eliminazione");
    }
  }

  async function downloadAdminFile(f) {
    if (!selected) return;
    try {
      const res = await api.get(`/admin/users/${selected.id}/files/${f.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: f.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Errore download");
    }
  }

  async function previewAdminFile(f) {
    if (!selected) return;
    try {
      const res = await api.get(`/admin/users/${selected.id}/files/${f.id}/preview`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: f.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Anteprima non disponibile");
    }
  }

  async function changeAccountType(newType) {
    if (!selected) return;
    if ((selected.account_type || "studio") === newType) return;
    const labels = { studio: "Studio Architetto", trader: "Trader / Rivenditore", real_estate: "Agenzia Immobiliare" };
    const ok = window.confirm(
      `Cambiare il tipo di account di ${selected.name || selected.email} da "${labels[selected.account_type || "studio"]}" a "${labels[newType]}"?\n\n` +
      "Lato utente nulla cambia visualmente. Le prossime logiche di pricing (prezzo studio vs trader) seguiranno il nuovo tipo."
    );
    if (!ok) return;
    setSavingAccountType(true);
    try {
      await api.put(`/admin/users/${selected.id}/account-type`, { account_type: newType });
      toast.success(`Tipo account aggiornato: ${labels[newType]}`);
      setSelected((prev) => prev ? { ...prev, account_type: newType } : prev);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento tipo");
    } finally {
      setSavingAccountType(false);
    }
  }

  async function resetUserPassword() {
    if (!selected) return;
    const pwd = window.prompt(
      `Inserisci una password temporanea per ${selected.name || selected.email}\n(minimo 8 caratteri)`,
      ""
    );
    if (pwd === null) return;
    if (pwd.length < 8) { toast.error("Almeno 8 caratteri"); return; }
    if (!window.confirm(`Confermi il reset della password per ${selected.email}?`)) return;
    setResettingPwd(true);
    try {
      await api.post(`/admin/users/${selected.id}/reset-password`, { new_password: pwd });
      // Show the temporary password so admin can communicate it to the user
      window.prompt(
        `Password reimpostata con successo!\nCondividi queste credenziali con ${selected.email}:\n\nEmail: ${selected.email}\nPassword temporanea (copia qui sotto):`,
        pwd
      );
      toast.success("Password reimpostata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore reset password");
    } finally {
      setResettingPwd(false);
    }
  }

  async function deleteUser() {
    if (!selected) return;
    // First confirm: explanation of cascade
    const step1 = window.confirm(
      `Eliminare definitivamente l'utente ${selected.name || selected.email}?\n\n` +
      "Verranno cancellati TUTTI i dati collegati:\n" +
      "• Progetti e relativi file allegati\n" +
      "• Cronologia attività e movimenti wallet\n" +
      "• Dichiarazioni d'intento / file riservati\n" +
      "• Richieste sconto e sconti personalizzati\n\n" +
      "Questa azione è IRREVERSIBILE."
    );
    if (!step1) return;
    // Second confirm: typing the email
    const typed = window.prompt(
      `Per confermare, digita esattamente l'email dell'utente:\n${selected.email}`,
      ""
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== (selected.email || "").toLowerCase()) {
      toast.error("Email non corrisponde. Eliminazione annullata.");
      return;
    }
    setDeletingUser(true);
    try {
      const { data } = await api.delete(`/admin/users/${selected.id}`);
      const d = data.deleted || {};
      toast.success(
        `Utente eliminato. Progetti: ${d.projects || 0} · File: ${d.project_files || 0} · Movimenti: ${d.reward_tx || 0}`
      );
      setUsers((prev) => prev.filter((x) => x.id !== selected.id));
      closePanel();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    } finally {
      setDeletingUser(false);
    }
  }

  async function sendReminder(u, e) {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!window.confirm(`Inviare email di richiamo a ${u.name || u.email}?`)) return;
    setReminderSending((s) => ({ ...s, [u.id]: true }));
    try {
      const { data } = await api.post(`/admin/users/${u.id}/remind`);
      const sent = data.sent;
      toast.success(
        sent
          ? `Richiamo inviato (${data.products_count} prodotti, ${data.assets_count} asset)`
          : "Richiamo registrato (Resend non configurato)"
      );
      // Update card with new last_reminder_at without full reload
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, last_reminder_at: data.last_reminder_at } : x));
    } catch {
      toast.error("Errore invio richiamo");
    } finally {
      setReminderSending((s) => { const n = { ...s }; delete n[u.id]; return n; });
    }
  }

  return (
    <div className="space-y-5" data-testid="admin-users">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">Utenti</div>
          <div className="font-display text-2xl text-ink">Utenti approvati</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] tracking-[0.15em] uppercase text-slate1">
            {filtered.length} {filtered.length === 1 ? "utente" : "utenti"}
          </span>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90"
            data-testid="admin-users-create"
            title="Crea un nuovo profilo utente (pre-approvato)"
          >
            <FilePlus size={13} /> Crea utente
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-ink/10 rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome, email o studio..."
            className="w-full border border-ink/20 pl-9 pr-3 py-2 rounded-sm bg-cream text-sm"
            data-testid="admin-users-search"
          />
        </div>
        <div className="inline-flex border border-ink/20 rounded-sm overflow-hidden">
          {[
            { k: "all", l: "Tutti" },
            { k: "studio", l: "Studi" },
            { k: "trader", l: "Trader" },
            { k: "real_estate", l: "Real Estate" },
            { k: "admin", l: "Admin" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setAccountType(t.k)}
              className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase ${accountType === t.k ? "bg-ink text-cream" : "bg-white text-slate1 hover:text-ink"}`}
              data-testid={`admin-users-filter-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-slate1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="accent-ink"
            data-testid="admin-users-inactive-toggle"
          />
          Solo inattivi (&gt; 2 mesi)
        </label>
      </div>

      {loading ? (
        <div className="bg-white border border-ink/10 rounded-sm p-6 text-sm text-slate1">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-ink/10 rounded-sm p-8 text-center" data-testid="admin-users-empty">
          <div className="text-sm text-slate1">Nessun utente corrisponde ai criteri selezionati.</div>
        </div>
      ) : (
        <>
          {accountType !== "trader" && accountType !== "real_estate" && accountType !== "admin" && studios.length > 0 && (
            <section>
              <div className="text-[11px] tracking-[0.25em] uppercase text-gold inline-flex items-center gap-2 mb-3">
                <Building2 size={12} /> Studi · {studios.length}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {studios.map((u) => <UserCard key={u.id} u={u} onOpen={openActivity} onSendReminder={sendReminder} reminderSending={reminderSending} />)}
              </div>
            </section>
          )}
          {accountType !== "studio" && accountType !== "real_estate" && accountType !== "admin" && traders.length > 0 && (
            <section className="mt-6">
              <div className="text-[11px] tracking-[0.25em] uppercase text-blue-700 inline-flex items-center gap-2 mb-3">
                <Briefcase size={12} /> Trader · {traders.length}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {traders.map((u) => <UserCard key={u.id} u={u} onOpen={openActivity} onSendReminder={sendReminder} reminderSending={reminderSending} />)}
              </div>
            </section>
          )}
          {accountType !== "studio" && accountType !== "trader" && accountType !== "admin" && realEstates.length > 0 && (
            <section className="mt-6">
              <div className="text-[11px] tracking-[0.25em] uppercase text-emerald-700 inline-flex items-center gap-2 mb-3">
                <Users size={12} /> Real Estate · {realEstates.length}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {realEstates.map((u) => <UserCard key={u.id} u={u} onOpen={openActivity} onSendReminder={sendReminder} reminderSending={reminderSending} />)}
              </div>
            </section>
          )}
          {(accountType === "all" || accountType === "admin") && admins.length > 0 && (
            <section className="mt-6" data-testid="admin-users-section-admin">
              <div className="text-[11px] tracking-[0.25em] uppercase text-violet-700 inline-flex items-center gap-2 mb-3">
                <ShieldCheck size={12} /> Admin · {admins.length}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {admins.map((u) => <UserCard key={u.id} u={u} onOpen={openActivity} onSendReminder={sendReminder} reminderSending={reminderSending} />)}
              </div>
            </section>
          )}
        </>
      )}

      {/* Activity drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-ink/40" onClick={closePanel}>
          <div
            className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-cream overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-user-activity-panel"
          >
            <div className="p-6 border-b border-ink/10 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className={`text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 ${
                  selected.account_type === "trader" ? "text-blue-700"
                  : selected.account_type === "real_estate" ? "text-emerald-700"
                  : "text-gold"
                }`}>
                  {selected.account_type === "trader"
                    ? <Briefcase size={11} />
                    : selected.account_type === "real_estate"
                    ? <Users size={11} />
                    : <Building2 size={11} />}
                  {selected.account_type === "trader"
                    ? "Trader"
                    : selected.account_type === "real_estate"
                    ? "Real Estate"
                    : "Studio"}
                </div>
                <div className="font-display text-xl text-ink truncate">{selected.name || selected.studio || selected.email}</div>
                <div className="text-xs text-slate1 truncate">{selected.email}</div>
                {/* Cambio tipo account: l'utente può essersi registrato come Studio ma essere in realtà un Trader. */}
                {selected.role !== "admin" && (
                  <div className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-slate1">
                    <span>Cambia tipo:</span>
                    <select
                      value={selected.account_type || "studio"}
                      onChange={(e) => changeAccountType(e.target.value)}
                      disabled={savingAccountType}
                      className="px-2 py-1 border border-ink/20 bg-white text-ink text-[11px] tracking-[0.15em] uppercase rounded-sm focus:outline-none focus:border-ink disabled:opacity-50"
                      data-testid="admin-user-account-type"
                    >
                      <option value="studio">Studio</option>
                      <option value="trader">Trader</option>
                      <option value="real_estate">Real Estate</option>
                    </select>
                    {savingAccountType && <span className="text-[10px] italic">Salvataggio…</span>}
                  </div>
                )}
              </div>
              <button onClick={closePanel} className="text-slate1 hover:text-ink" data-testid="admin-user-activity-close">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setShowEditModal(true)}
                  disabled={resettingPwd || deletingUser}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-ink/20 rounded-sm text-[11px] tracking-[0.2em] uppercase text-ink hover:bg-ink hover:text-cream disabled:opacity-50 transition-colors"
                  data-testid="admin-user-edit"
                >
                  <Pencil size={13} /> Modifica dati
                </button>
                <button
                  onClick={resetUserPassword}
                  disabled={resettingPwd || deletingUser}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-ink/20 rounded-sm text-[11px] tracking-[0.2em] uppercase text-ink hover:bg-ink hover:text-cream disabled:opacity-50 transition-colors"
                  data-testid="admin-user-reset-pwd"
                >
                  <KeyRound size={13} /> {resettingPwd ? "Reset..." : "Reimposta password"}
                </button>
                <button
                  onClick={deleteUser}
                  disabled={resettingPwd || deletingUser}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-rose-300 rounded-sm text-[11px] tracking-[0.2em] uppercase text-rose-700 hover:bg-rose-600 hover:text-white hover:border-rose-600 disabled:opacity-50 transition-colors"
                  data-testid="admin-user-delete"
                >
                  <Trash size={13} /> {deletingUser ? "Eliminazione..." : "Elimina utente"}
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div className="bg-white border border-ink/10 rounded-sm p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Progetti</div>
                  <div className="mt-1 font-display text-xl text-ink">{selected.project_count}</div>
                </div>
                <div className="bg-white border border-ink/10 rounded-sm p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Valorizzato</div>
                  <div className="mt-1 font-display text-xl text-ink">€ {Math.round(selected.valorized_total || 0).toLocaleString("it-IT")}</div>
                </div>
                <div className="bg-white border border-ink/10 rounded-sm p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Volume chiuso</div>
                  <div className="mt-1 font-display text-xl text-ink">€ {Math.round(selected.closed_total).toLocaleString("it-IT")}</div>
                </div>
                <div className="bg-white border border-ink/10 rounded-sm p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Ultimo accesso</div>
                  <div className="mt-1 text-sm text-ink">{selected.last_login_at ? fmtDate(selected.last_login_at) : "Mai"}</div>
                </div>
              </div>

              {(selected.valorized_total || 0) > 0 && (
                <SuccessRateRing
                  rate={selected.success_rate || 0}
                  closed={selected.closed_total}
                  valorized={selected.valorized_total}
                />
              )}

              {selected.account_type === "trader" && (
                <div data-testid="admin-user-files">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-3 inline-flex items-center gap-2">
                    <Lock size={12} /> Dichiarazioni d&apos;intento · Solo admin
                  </div>

                  <label className={`block border-2 border-dashed border-ink/20 rounded-sm p-4 text-center cursor-pointer mb-3 hover:bg-white transition-colors ${uploadingFile ? "opacity-60 pointer-events-none" : ""}`}>
                    <Upload size={16} className="inline mr-2 text-slate1" />
                    <span className="text-[11px] tracking-[0.2em] uppercase text-slate1">
                      {uploadingFile ? "Caricamento..." : "Carica file (PDF, max 25 MB)"}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      disabled={uploadingFile}
                      onChange={(e) => uploadAdminFile(e.target.files?.[0])}
                      data-testid="admin-user-file-upload"
                    />
                  </label>

                  {adminFilesLoading ? (
                    <div className="text-sm text-slate1">Caricamento...</div>
                  ) : adminFiles.length === 0 ? (
                    <div className="bg-white border border-ink/10 rounded-sm p-4 text-sm text-slate1 text-center" data-testid="admin-user-files-empty">
                      Nessuna dichiarazione d&apos;intento caricata.
                    </div>
                  ) : (
                    <ul className="space-y-2" data-testid="admin-user-files-list">
                      {adminFiles.map((f) => (
                        <li key={f.id} className="bg-white border border-ink/10 rounded-sm p-3 flex items-center gap-3" data-testid={`admin-user-file-${f.id}`}>
                          <FileText size={14} className="text-slate1 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-ink truncate">{f.title || f.filename}</div>
                            <div className="text-[11px] text-slate1">
                              {f.size > 0 && <>{(f.size / 1024 / 1024).toFixed(2)} MB · </>}
                              Caricato il {new Date(f.created_at).toLocaleDateString("it-IT")}
                            </div>
                          </div>
                          <button onClick={() => previewAdminFile(f)} className="text-slate1 hover:text-ink" aria-label="Anteprima" data-testid={`admin-user-file-preview-${f.id}`}>
                            <Eye size={14} />
                          </button>
                          <button onClick={() => downloadAdminFile(f)} className="text-slate1 hover:text-ink" aria-label="Download" data-testid={`admin-user-file-download-${f.id}`}>
                            <Download size={14} />
                          </button>
                          <button onClick={() => deleteAdminFile(f.id)} className="text-slate1 hover:text-destructive" aria-label="Elimina" data-testid={`admin-user-file-delete-${f.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Fornitori & scontistica riservata assegnata a questo utente */}
              <div data-testid="admin-user-reserved-discounts">
                <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-3 inline-flex items-center gap-2">
                  <Tag size={12} /> Fornitori &amp; scontistica riservata
                </div>
                {userDiscountsLoading ? (
                  <div className="text-sm text-slate1">Caricamento...</div>
                ) : userDiscounts.length === 0 ? (
                  <div className="bg-white border border-ink/10 rounded-sm p-4 text-sm text-slate1 text-center" data-testid="admin-user-reserved-discounts-empty">
                    Nessuno sconto riservato assegnato a questo utente.
                  </div>
                ) : (
                  <ul className="space-y-1.5" data-testid="admin-user-reserved-discounts-list">
                    {userDiscounts.map((d) => {
                      const fornitore = Math.round((d.purchase_discount_pct || 0) * 100) / 100;
                      const utente = Math.round((d.discount_pct || 0) * 100) / 100;
                      return (
                        <li
                          key={d.supplier_id}
                          className="bg-white border border-ink/10 rounded-sm px-3 py-2 flex items-center gap-3"
                          data-testid={`reserved-discount-${d.supplier_id}`}
                        >
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-ink truncate">{d.supplier_name}</span>
                            {d.admin_note && (
                              <span className="block text-[11px] text-slate1 truncate" title={d.admin_note}>{d.admin_note}</span>
                            )}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="text-center" title="Sconto che il fornitore pratica a te (acquisto)">
                              <div className="text-[9px] tracking-[0.1em] uppercase text-slate1 mb-0.5">Dal fornitore</div>
                              <div
                                className="text-[11px] font-bold text-ink bg-ink/5 border border-ink/15 rounded px-1.5 py-0.5"
                                data-testid={`reserved-discount-${d.supplier_id}-purchase`}
                              >
                                {fornitore > 0 ? `${fornitore}%` : "—"}
                              </div>
                            </div>
                            <span className="text-slate1 text-xs">→</span>
                            <div className="text-center" title="Sconto riservato che pratichi a questo utente">
                              <div className="text-[9px] tracking-[0.1em] uppercase text-gold mb-0.5">All&apos;utente</div>
                              <div
                                className="text-[11px] font-bold text-ink bg-gold/15 border border-gold/30 rounded px-1.5 py-0.5"
                                data-testid={`reserved-discount-${d.supplier_id}-user`}
                              >
                                {utente}%
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Fornitori più consultati (aperture + download listini) */}
              {activity?.supplier_stats?.length > 0 && (
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3 inline-flex items-center gap-2">
                    <Store size={12} /> Fornitori più consultati
                  </div>
                  <ul className="space-y-1.5" data-testid="admin-user-supplier-stats">
                    {activity.supplier_stats.map((s) => (
                      <li
                        key={s.supplier_id}
                        className="bg-white border border-ink/10 rounded-sm px-3 py-2 flex items-center gap-3"
                        data-testid={`supplier-stat-${s.supplier_id}`}
                      >
                        <span className="flex-1 text-sm text-ink truncate">{s.supplier_name}</span>
                        <span className="text-[11px] text-slate1 inline-flex items-center gap-1" title="Aperture listini">
                          <Eye size={11} /> {s.opens}
                        </span>
                        <span className="text-[11px] text-slate1 inline-flex items-center gap-1" title="Download">
                          <Download size={11} /> {s.downloads}
                        </span>
                        <span className="text-[11px] font-bold text-ink bg-gold/15 border border-gold/30 rounded px-1.5 py-0.5" title="Totale consultazioni">
                          {s.total}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-3 inline-flex items-center gap-2">
                  <Activity size={12} /> Ultime 10 attività
                </div>

                {activityLoading ? (
                  <div className="text-sm text-slate1">Caricamento...</div>
                ) : !activity || activity.activities.length === 0 ? (
                  <div className="bg-white border border-ink/10 rounded-sm p-5 text-sm text-slate1 text-center" data-testid="admin-user-activity-empty">
                    Nessuna attività registrata per questo utente.
                  </div>
                ) : (
                  <ul className="space-y-2" data-testid="admin-user-activity-list">
                    {activity.activities.map((a) => {
                      const meta = ACTION_META[a.action] || { label: a.action, icon: Activity };
                      const Icon = meta.icon;
                      return (
                        <li key={a.id || `${a.action}-${a.created_at}`} className="bg-white border border-ink/10 rounded-sm p-3 flex items-start gap-3">
                          <div className="mt-0.5 text-slate1"><Icon size={14} /></div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-ink">{meta.label}</div>
                            <div className="text-xs truncate"><ActivityDescription a={a} /></div>
                          </div>
                          <div className="text-[10px] tracking-[0.15em] uppercase text-slate1 whitespace-nowrap">
                            {fmtDate(a.created_at)}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create user modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); reload(); }}
        />
      )}

      {/* Edit user modal */}
      {showEditModal && selected && (
        <EditUserModal
          user={selected}
          onClose={() => setShowEditModal(false)}
          onSaved={(patch) => {
            setSelected((s) => ({ ...s, ...patch }));
            setUsers((prev) => prev.map((u) => (u.id === selected.id ? { ...u, ...patch } : u)));
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Modal admin per correggere i dati anagrafici di un utente esistente
 * (nome, studio/azienda/agenzia, email, telefono).
 */
function EditUserModal({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [studio, setStudio] = useState(user.studio || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [saving, setSaving] = useState(false);

  const companyLabel = user.role === "admin"
    ? "Studio / Azienda"
    : user.account_type === "real_estate"
    ? "Agenzia"
    : user.account_type === "trader"
    ? "Azienda"
    : "Studio";

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (name.trim().length < 2) {
      toast.error("Il nome deve avere almeno 2 caratteri");
      return;
    }
    setSaving(true);
    try {
      const patch = {
        name: name.trim(),
        studio: studio.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
      };
      await api.put(`/admin/users/${user.id}`, patch);
      toast.success("Dati utente aggiornati");
      onSaved(patch);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio dati");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="edit-user-modal">
      <div className="bg-cream max-w-lg w-full rounded-sm shadow-xl border border-ink/20" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-ink/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate1">Admin</div>
            <h2 className="font-display text-xl text-ink mt-0.5">Modifica dati utente</h2>
          </div>
          <button onClick={onClose} className="text-slate1 hover:text-ink" data-testid="edit-user-close"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Nome completo *</label>
              <input
                type="text"
                required
                minLength={2}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
                data-testid="edit-user-name"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">{companyLabel}</label>
              <input
                type="text"
                value={studio}
                onChange={(e) => setStudio(e.target.value)}
                className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
                data-testid="edit-user-studio"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Email *</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
              data-testid="edit-user-email"
            />
            {email.trim().toLowerCase() !== (user.email || "").toLowerCase() && (
              <p className="mt-1 text-[11px] text-amber-700">
                ⚠ L&apos;utente dovrà accedere con la nuova email. La password resta invariata.
              </p>
            )}
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Telefono</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
              data-testid="edit-user-phone"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 disabled:opacity-50"
              data-testid="edit-user-save"
            >
              {saving ? "Salvataggio..." : "Salva modifiche"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-5 border border-ink/20 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/5 disabled:opacity-50"
              data-testid="edit-user-cancel"
            >
              Annulla
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Modal admin per creare un nuovo profilo utente pre-approvato.
 * L'utente potrà loggare immediatamente con le credenziali generate.
 * L'admin comunica voce/email-manuale (no email automatica).
 */
function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [studio, setStudio] = useState("");
  const [phone, setPhone] = useState("");
  const [accountType, setAccountType] = useState("studio");
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // {email, password, role} dopo successo
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Verifica se l'utente corrente può creare admin (whitelist SUPERADMIN_EMAILS)
  useEffect(() => {
    api.get("/admin/me/is-super-admin")
      .then((r) => setIsSuperAdmin(!!r.data?.is_super_admin))
      .catch(() => setIsSuperAdmin(false));
  }, []);

  // Genera una password sicura di 12 chars (alfanumerica + simboli base)
  const generatePassword = () => {
    const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$";
    const len = 12;
    let p = "";
    const a = new Uint32Array(len);
    crypto.getRandomValues(a);
    for (let i = 0; i < len; i += 1) {
      p += charset[a[i] % charset.length];
    }
    setPassword(p);
    setShowPwd(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password troppo corta (min 6 caratteri)");
      return;
    }
    // Telefono obbligatorio solo per utenti non-admin
    if (!isAdmin && phone.trim().length < 3) {
      toast.error("Telefono obbligatorio per utenti non admin");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/users", {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
        studio: studio.trim(),
        phone: phone.trim(),
        account_type: isAdmin ? "admin" : accountType,
        is_admin: isAdmin,
      });
      setCreated({ email: email.trim().toLowerCase(), password, role: isAdmin ? "admin" : "architect" });
      toast.success(isAdmin ? "Admin creato" : "Utente creato e pre-approvato");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore creazione utente";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const copyCredentials = async () => {
    if (!created) return;
    const text = `Email: ${created.email}\nPassword: ${created.password}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Credenziali copiate negli appunti");
    } catch {
      toast.error("Impossibile copiare automaticamente");
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-cream max-w-lg w-full rounded-sm shadow-xl border border-ink/20" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-ink/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate1">Admin</div>
            <h2 className="font-display text-xl text-ink mt-0.5">Crea nuovo profilo utente</h2>
          </div>
          <button onClick={onClose} className="text-slate1 hover:text-ink" data-testid="create-user-close"><X size={20} /></button>
        </div>

        {created ? (
          /* SUCCESS STATE: mostra le credenziali per la comunicazione manuale */
          <div className="p-6 space-y-5">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
              <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-800 mb-1">Utente creato con successo</div>
              <p className="text-sm text-emerald-900">Comunica queste credenziali manualmente all&apos;utente (email/voce/SMS). L&apos;utente potrà cambiare la password dalla sua area riservata.</p>
            </div>
            <div className="bg-white border border-ink/15 rounded-sm p-4 space-y-3 font-mono text-sm">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1">Email</div>
                <div className="text-ink select-all" data-testid="created-email">{created.email}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1">Password temporanea</div>
                <div className="text-ink select-all" data-testid="created-password">{created.password}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={copyCredentials} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-ink/20 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/5" data-testid="copy-credentials">
                Copia credenziali
              </button>
              <button onClick={onCreated} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90" data-testid="created-close">
                Chiudi
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Email *</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="cliente@studio.it"
                className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
                data-testid="create-user-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Nome completo *</label>
                <input
                  type="text"
                  required
                  minLength={2}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Mario Rossi"
                  className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
                  data-testid="create-user-name"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">
                  Telefono {isAdmin ? "" : "*"}
                </label>
                <input
                  type="tel"
                  required={!isAdmin}
                  minLength={isAdmin ? 0 : 3}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={isAdmin ? "+39 333 1234567 (opzionale)" : "+39 333 1234567"}
                  className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
                  data-testid="create-user-phone"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Studio / Azienda</label>
              <input
                type="text"
                value={studio}
                onChange={(e) => setStudio(e.target.value)}
                placeholder="Studio Rossi Architetti"
                className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
                data-testid="create-user-studio"
              />
            </div>

            {/* Toggle: crea come ADMIN — visibile solo ai super-admin (whitelist env) */}
            {isSuperAdmin ? (
              <div className="border border-gold/40 bg-gradient-to-br from-gold/5 to-transparent rounded-sm p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAdmin}
                    onChange={(e) => setIsAdmin(e.target.checked)}
                    className="mt-1 accent-gold"
                    data-testid="create-user-is-admin"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-ink">Crea come amministratore ARCHI-HUB</div>
                    <div className="text-[11px] text-slate1 mt-0.5">
                      L&apos;utente potrà gestire tutto ARCHI-HUB (utenti, prodotti, briefing).
                      Avrà un <b>IMTask CRM dedicato</b>, con dati completamente separati dagli altri admin.
                    </div>
                  </div>
                </label>
              </div>
            ) : (
              <div className="border border-ink/10 bg-ink/5 rounded-sm p-3 text-[11px] text-slate1" data-testid="create-user-not-super-admin">
                <b>Nota:</b> per creare altri amministratori serve essere <i>super-admin</i>. La lista dei super-admin è configurata dal server (variabile <code className="bg-ink/10 px-1 rounded">SUPERADMIN_EMAILS</code>). Puoi comunque creare utenti architect/trader/immobiliare.
              </div>
            )}

            {!isAdmin && (
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Tipo account</label>
                <select
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                  className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
                  data-testid="create-user-account-type"
                >
                  <option value="studio">Studio Architetto</option>
                  <option value="trader">Trader / Rivenditore</option>
                  <option value="real_estate">Real Estate / Agenzia</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Password iniziale * (min 6 caratteri)</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showPwd ? "text" : "password"}
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password..."
                    className="w-full px-3 py-2 pr-10 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold font-mono"
                    data-testid="create-user-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate1 hover:text-ink"
                    tabIndex={-1}
                  >
                    {showPwd ? <Eye size={14} /> : <Lock size={14} />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={generatePassword}
                  className="px-3 py-2 border border-ink/20 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/5 shrink-0"
                  data-testid="create-user-generate-password"
                  title="Genera una password sicura casuale"
                >
                  Genera
                </button>
              </div>
            </div>
            <div className={`${isAdmin ? "bg-gold/10 border-gold/30" : "bg-cream2 border-ink/10"} border rounded-sm p-3 text-xs text-slate1`}>
              {isAdmin ? (
                <>Verrà creato un <b>ADMIN ARCHI-HUB</b> pre-approvato con IMTask dedicato. La password va comunicata <b>manualmente</b>. L&apos;admin potrà cambiarla dalla sua area riservata.</>
              ) : (
                <>L&apos;utente sarà <b>pre-approvato</b> e potrà loggare immediatamente. La password va comunicata <b>manualmente</b> (no email automatica). L&apos;utente potrà cambiarla dalla sua area riservata.</>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-ink/20 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/5">Annulla</button>
              <button
                type="submit"
                disabled={saving}
                className={`inline-flex items-center gap-2 px-5 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-50 ${
                  isAdmin
                    ? "bg-gold text-white hover:bg-amber-600"
                    : "bg-ink text-cream hover:bg-ink/90"
                }`}
                data-testid="create-user-submit"
              >
                {saving ? "Creazione..." : (isAdmin ? "Crea admin" : "Crea utente")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
